import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import { Button } from "../ui/button";
import { MarketingLink } from "@/components/OptionalLink";
import { useRouter } from "next/router";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useSession } from "@/components/useSession";
import { capitalize, cn, fetcherIgnore404 } from "@/lib/utils";
import { User } from "@/types";
import { JSONSafe } from "@/types";
import useSWR from "swr";
import logger from "@/lib/logger";
import { AlignJustify } from "lucide-react";
import React from "react";

interface RouteProps {
  id: string;
  href: string;
  label: string;
  active: boolean;
  primary?: boolean;
  overflow?: boolean | "only";
  className?: string;
  blur?: boolean;
}

const getMenu = (path: string, signedIn: boolean): RouteProps[] => [
  {
    id: "home",
    href: "/#stay",
    label: "Home",
    active: path === "/",
    overflow: true
  },
  {
    id: "pricing",
    href: "/pricing",
    label: "Pricing",
    active: path === "/pricing",
    overflow: true
  },
  {
    id: "templates",
    href: "/r",
    label: "Templates",
    active: path === "/r",
    overflow: true
  },
  {
    id: "blog",
    href: "/blog",
    label: "Blog",
    active: path === "/blog",
    overflow: true
  },
  {
    id: "team",
    href: "/team",
    label: "About us",
    active: path === "/team",
    overflow: true
  },
  {
    id: "docs",
    href: "https://docs.dollardeploy.com",
    label: "Documentation",
    active: path === "/docs",
    overflow: "only"
  },
  ...(signedIn
    ? [
        {
          id: "dashboard",
          href: "/dashboard",
          label: "Dashboard",
          active: path === "/dashboard",
          primary: true,
          className: "md:min-w-[110px]",
          blur: true
        }
      ]
    : [
        {
          id: "signUp",
          href: "/auth/signUp?callbackUrl=/dashboard",
          label: "Sign Up",
          active: path === "/auth/signUp",
          primary: true,
          className: "md:min-w-[110px]",
          blur: true
        },
        {
          id: "signIn",
          href: "/auth/signIn?callbackUrl=/dashboard",
          label: "Sign In",
          active: path === "/auth/signIn",
          blur: true
        }
      ])
];

/** Icon only */
export const Icon = ({
  dark = false,
  light = false,
  className = "",
  counter
}: {
  dark?: boolean;
  light?: boolean;
  className?: string;
  counter?: number;
}) => (
  <div className="flex flex-row items-center justify-center relative">
    <img
      src="/logo.svg"
      className={cn("h-5", dark ? "" : light ? "invert-100" : "dark:invert-100", className)}
      alt="DollarDeploy"
    />
    {counter && (
      <span className="absolute bottom-1 right-1 text-[10px] bg-primary border border-secondary text-primary-foreground rounded-full px-1">
        {counter}
      </span>
    )}
  </div>
);

/** Icon + Brand name */
export const Logo = ({ className }: { className?: string }) => (
  <div>
    <img
      src="/logo-main.svg"
      className={cn("h-6 dark:invert-100", className)}
      alt="DollarDeploy"
    />
  </div>
);

export const applyFlags = (user?: Pick<User, "flags">) => {
  if (!user) {
    return;
  }

  if (user.flags.includes("tracking-")) {
    if (!localStorage.getItem("plausible_ignore")) {
      logger.info("Disabling tracking");
      localStorage.setItem("plausible_ignore", "true");
    }
  } else if (localStorage.getItem("plausible_ignore")) {
    logger.info("Enabling tracking");
    localStorage.removeItem("plausible_ignore");
  }
};

export const Navbar = ({ themeToggle = true }: { themeToggle?: boolean }) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const { data: user, isLoading } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  applyFlags(user);
  const [isOpen, setIsOpen] = React.useState(false);

  const currentMenu = getMenu(router.pathname, !!session?.user);

  return (
    <header className="fixed border-b top-0 z-50 w-full bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
      <NavigationMenu className="mx-auto">
        <NavigationMenuList className="pl-4 pr-2 md:px-3 container h-14 w-screen flex justify-between items-center">
          <NavigationMenuItem>
            <MarketingLink
              rel="noreferrer noopener"
              href="/"
              className="font-bold text-md md:text-xl plausible-event-name=Home"
            >
              <Logo />
            </MarketingLink>
          </NavigationMenuItem>

          <nav className="hidden md:flex gap-2">
            {currentMenu
              .filter(route => route.overflow !== "only")
              .map((route: RouteProps) => (
                <Button
                  asChild
                  rel="noreferrer noopener"
                  key={route.id}
                  size="sm"
                  variant={route.primary ? "default" : "ghost"}
                  className={cn(
                    `plausible-event-name=${capitalize(route.id)}`,
                    route.className,
                    route.blur && (isLoading || status === "loading")
                      ? "transition-filter duration-500 blur-xs"
                      : "transition-filter duration-500 blur-none"
                  )}
                >
                  <MarketingLink href={route.href}>{route.label}</MarketingLink>
                </Button>
              ))}

            {themeToggle && <ThemeToggle />}
          </nav>

          <div className="flex md:hidden items-center gap-0">
            {currentMenu
              .filter(route => !route.overflow)
              .map(route => (
                <Button
                  asChild
                  rel="noreferrer noopener"
                  key={route.id}
                  size="sm"
                  variant={route.primary ? "default" : "outline"}
                  className={cn(
                    "mr-2",
                    route.className,
                    `plausible-event-name=${capitalize(route.id)}`
                  )}
                >
                  <MarketingLink href={route.href}>{route.label}</MarketingLink>
                </Button>
              ))}

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="h-7 w-7">
                  <AlignJustify className="h-4 w-4" />
                  <span className="sr-only">Toggle Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-4">
                  {currentMenu
                    .filter(route => !route.primary)
                    .map(route => (
                      <Button
                        asChild
                        key={route.id}
                        onClick={() => setIsOpen(false)}
                        variant={route.primary ? "default" : "ghost"}
                        className={cn(`plausible-event-name=${capitalize(route.id)}`)}
                      >
                        <MarketingLink href={route.href}>{route.label}</MarketingLink>
                      </Button>
                    ))}
                </nav>
              </SheetContent>
            </Sheet>

            {themeToggle && <ThemeToggle />}
          </div>
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};
